import {
  CSV_DOWNLOAD_QUERY_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_QUERY_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_TABLESTORE_MIMETYPE,
  CSV_DOWNLOAD_TABLESTORE_MIMETYPE,
  CSV_EXPORT_MIMETYPE,
  EXECUTION_LOG_MIMETYPE,
  KernelOutputTypes,
  PARQUET_EXPORT_MIMETYPE,
  PREVIEW_QUERY_MIMETYPE,
  TABLESTORE_EXPORT_MIMETYPE,
} from "@hex/common";

import { OutputExportInfo } from "./exportTypes.js";

/**
 * Old mimetypes where the export contents must be parsed to determine
 * success/failure. Contents are JSON with a `success` discriminant field.
 */
const OLD_EXPORT_MIMETYPES = new Set<string>([
  CSV_EXPORT_MIMETYPE,
  PARQUET_EXPORT_MIMETYPE,
  TABLESTORE_EXPORT_MIMETYPE,
]);

/**
 * New CSV_DOWNLOAD_* mimetypes (and PREVIEW_QUERY) where the error is always
 * null — the presence of the mimetype is sufficient signal.
 */
const NEW_EXPORT_MIMETYPES = new Set<string>([
  CSV_DOWNLOAD_QUERY_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_QUERY_MIMETYPE,
  CSV_DOWNLOAD_TABLESTORE_MIMETYPE,
  CSV_DOWNLOAD_SOURCE_TABLESTORE_MIMETYPE,
  PREVIEW_QUERY_MIMETYPE,
]);

/**
 * Extracts {@link OutputExportInfo} entries from a list of output contents.
 * Only contents with `status === "READY"` are considered. Unrecognized
 * mimetypes are skipped.
 */
export function getOutputExportInfo(
  outputContents: readonly {
    mimeType: string;
    contents: string | undefined;
    status: string;
  }[],
): OutputExportInfo[] {
  return outputContents.flatMap((oc) => {
    if (oc.status !== "READY") {
      return [];
    }

    if (OLD_EXPORT_MIMETYPES.has(oc.mimeType)) {
      if (oc.contents == null) {
        return [];
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(oc.contents);
      } catch {
        return [];
      }
      const failure =
        KernelOutputTypes.ExportOutputContentsFailure.safeParse(parsed);
      const error = failure.success ? failure.data.reason : null;
      return [{ mimeType: oc.mimeType, error, exportLabel: null }];
    }

    if (NEW_EXPORT_MIMETYPES.has(oc.mimeType)) {
      return [{ mimeType: oc.mimeType, error: null, exportLabel: null }];
    }

    if (oc.mimeType === EXECUTION_LOG_MIMETYPE) {
      if (oc.contents == null) {
        return [];
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(oc.contents);
      } catch {
        return [];
      }
      const result = KernelOutputTypes.ExecutionLogPayload.safeParse(parsed);
      if (!result.success) {
        return [];
      }
      // Synthesize each error entry as a tablestore-mimetype OutputExportInfo
      // so it flows through MIMETYPE_TO_MODE the same way a real
      // csvdownloadtablestore display would. Entries whose `extra.outputType`
      // doesn't map (currently only other option is "PREVIEW") are skipped.
      const outputExportInfos: OutputExportInfo[] = [];
      for (const entry of result.data.errors) {
        let mimeType: string;
        if (entry.extra?.["outputType"] === "DEFAULT") {
          mimeType = CSV_DOWNLOAD_TABLESTORE_MIMETYPE;
        } else if (entry.extra?.["outputType"] === "SOURCE") {
          mimeType = CSV_DOWNLOAD_SOURCE_TABLESTORE_MIMETYPE;
        } else {
          continue;
        }
        const label = entry.extra?.["exportLabel"];
        outputExportInfos.push({
          mimeType,
          error: entry.error,
          exportLabel: typeof label === "string" ? label : null,
        });
      }
      return outputExportInfos;
    }

    return [];
  });
}
